import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { $$ } from "@tento-ui/fastdom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { SizeGate } from "@tento-ui/ui/size-gate";
import { ScrollEngine } from "../engine/scroll-engine";
import { Scrollable } from "../engine/scrollable";

@component({
  name: "std-scroll-vertical",
})
export class ScrollVertical<T extends HTMLElement> extends LonaWebComponent {
  // @init
  private delegate: Option<ScrollVertical.Delegate<T>>;
  private _scrollEngine: Option<ScrollEngine<T>>;
  get scrollEngine(): ScrollEngine<T> {
    return this._scrollEngine!;
  }

  private scrollEngineDelegate(
    delegate: ScrollVertical.Delegate<T>,
  ): ScrollEngine.Delegate<T> {
    return {
      onCreate: () => {
        const $e = delegate.onCreate();
        this.appendChild($e);
        return $e;
      },
      onBind: ($e, index, position, heightPx, resizeOnly) => {
        const offsetPx = position;
        $e.toggleAttribute("recycled", false);
        if (delegate.options?.cellWidthForIndex) {
          $e.toggleAttribute("unmeasured", heightPx == null);
        }
        delegate.onBind(
          $e,
          index,
          heightPx ?? this.layoutProps!.cellWidthPx,
          resizeOnly,
        );
        if (heightPx) {
          $e.assignStyles({
            "--idx": String(index),
            height: heightPx + "px",
            "--offset": offsetPx + "px",
          });
        } else {
          $e.assignStyles({
            "--idx": String(index),
            "--offset": offsetPx + "px",
          });
        }
      },
      onRecycle: ($e) => {
        $e.toggleAttribute("recycled", true);
        delegate.onRecycle?.($e);
      },
      options: {
        ...delegate.options,
        onNegativeOverflow: (overflow: number) => {
          this.updateOverflowBanner(overflow);
          delegate.onNegativeOverflow?.(overflow);
        },
      },
    };
  }

  // @props
  private layoutProps: Option<ScrollEngine.LayoutProps>;

  // @components
  private $contentViewport = this.$("content-viewport");
  private $content = this.$("content");
  private $overflowBanner: HTMLElement = this.$("overflow-banner");
  get $viewport(): HTMLElement {
    return this.$contentViewport;
  }
  private $scrollable: Option<Scrollable.Dom>;

  // @state
  private size: Option<Size>;
  private sizeGate = new SizeGate();
  onSizeChangeTarget = this.$contentViewport;
  onSizeChange: Option<(s: Size) => void> = (s: Size) => {
    const width = s.width || this.offsetWidth;
    const height = s.height || this.offsetHeight;
    this.size = { width, height };
    this.sizeGate.resolve(this.size);
    if (this._scrollEngine && this.layoutProps && height > 0) {
      this.bindLayout({ ...this.layoutProps, length: height });
      this._scrollEngine.rebindViewport();
    }
    this.updateMaxContentOffset();
  };

  private updateOverflowBanner(overflow: number): void {
    this.$overflowBanner.style.display = overflow > 0 ? "block" : "none";
  }

  initialize(delegate: ScrollVertical.Delegate<T>) {
    this.delegate = delegate;
    this.$scrollable = new Scrollable.Dom(
      this.$contentViewport,
      {
        onScroll: (info) => {
          if (!this.$scrollable || !this._scrollEngine) return;
          delegate.onScroll?.({
            left: info.left,
            top: info.top,
            scrollHeight: this.computeLogicalHeight(),
          });
        },
        snapping: {
          onSnap: () => {},
          enabled: () => false,
        },
        // Vertical lists don't carry the horizontal sheet's hover storm;
        // hit-test suppression stays a no-op here until measured otherwise.
        onWheelActivity: () => {},
      },
      "vertical",
      0,
    );
    this._scrollEngine = new ScrollEngine(
      this.$scrollable,
      this.scrollEngineDelegate(delegate),
    );
    GESTURE_MANAGER.addPointerEvent(this.$overflowBanner, {
      onClick: () => this.renormalize(),
    });
  }

  /** Permanently release the current adapter ownership. A later initialize
   *  creates a new engine; an already waiting bind cannot target that engine. */
  teardown(): void {
    const engine = this._scrollEngine;
    const scrollable = this.$scrollable;
    // Relinquish ownership before callbacks so repeated/reentrant cleanup is
    // harmless and delayed browser input cannot reach the retired engine.
    this.delegate = null;
    this._scrollEngine = null;
    this.$scrollable = null;
    this.layoutProps = null;
    if (scrollable) scrollable.delegate = null;
    engine?.dispose();
    engine?.resetCells(($e) => $e.remove());
  }

  async bind(props: ScrollVertical.Props) {
    const engine = this._scrollEngine;
    const size = await this.sizeGate.waitForSize();
    if (!engine || this._scrollEngine !== engine) return;
    const cellHeightPx = props.cellHeightPx ?? Math.round(size.height / 2);
    this.bindLayout(
      {
        startOccludedPx: props.topOccludedPx ?? 0,
        endOccludedPx: props.bottomOccludedPx ?? 0,
        length: size.height,
        cellWidthPx: cellHeightPx,
        maxIndex: props.maxIndex,
        minIndex: props.minIndex ?? 0,
      },
    );
    // `contextDirtied` is the re-anchor: re-origin the map, move to the initial
    // index, rebind every rendered row. Without it this bind passed NO options
    // at all, which resolved to a `reveal` at the old bag's defaults — it moved
    // the view and left the map and the content alone.
    this.scrollEngine.bindIndex(
      props.initialIndex ?? 0,
      props.contextDirtied
        ? {
          kind: "reanchor",
          at: {
            position: "start",
            scrollBehavior: "auto",
            useNativeScrollTo: false,
          },
          viewportChangeSource: "bind",
        }
        : {
          kind: "reveal",
          at: {
            position: "start",
            scrollBehavior: "auto",
            useNativeScrollTo: false,
          },
          viewportChangeSource: "bind",
        },
    );
    this.updateMaxContentOffset();
  }

  /** No space parameter, unlike the horizontal scroller's: a vertical list's
   *  indices are row positions in one list, so index 3 means row 3 under every
   *  layout this component can be handed. That is exactly the claim
   *  {@link ScrollEngine.IndexSpace.identity} makes. */
  bindLayout(props: ScrollEngine.LayoutProps) {
    this.layoutProps = props;
    this.scrollEngine.bindLayout(props, ScrollEngine.IndexSpace.identity());
    this.updateMaxContentOffset();
    $$.mutate(() => {
      this.style.setProperty("--default-cell-length", props.cellWidthPx + "px");
    });
  }

  updateMaxIndex(maxIndex: number): void {
    if (!this.layoutProps) return;
    this.layoutProps = { ...this.layoutProps, maxIndex };
    this.scrollEngine.bindLayout(
      this.layoutProps,
      ScrollEngine.IndexSpace.identity(),
    );
    this.updateMaxContentOffset();
  }

  invalidateHeight(index: number, anchor: boolean = true): void {
    this._scrollEngine?.invalidateHeight(index, anchor);
    this.updateMaxContentOffset();
  }

  /**
   * Drain every cell — live and pooled — recycling each, handing it to
   * `dispose`, and detaching it from the light DOM. The next `bind()`
   * repopulates through the delegate's factory; until then the list is
   * empty. The verb exists for hosts whose cell shape changes wholesale
   * (see {@link ScrollEngine.resetCells}).
   */
  resetCells(dispose: ($e: T) => void): void {
    this._scrollEngine?.resetCells(($e) => {
      dispose($e);
      $e.remove();
    });
  }

  private computeLogicalHeight(): number {
    if (!this._scrollEngine || !this.layoutProps) return 0;
    const w = this._scrollEngine.window;
    const maxIdx = this.layoutProps.maxIndex ?? w.maxIndex;
    const lastHeight = this._scrollEngine.cellWidth.get(maxIdx);
    const maxIdxOffset = w.getOffset(maxIdx);
    if (maxIdxOffset != null) {
      return maxIdxOffset + lastHeight;
    }
    const defaultH = this._scrollEngine.cellWidth._default ?? lastHeight;
    return (maxIdx + 1) * defaultH;
  }

  private updateMaxContentOffset(): void {
    if (!this.$scrollable || !this.size) return;
    const logicalHeight = this.computeLogicalHeight();
    const contentHeight = Math.max(logicalHeight, this.size.height);
    this.$content.style.height = contentHeight + "px";
  }

  get listScrollTop(): number {
    return this.$contentViewport.scrollTop;
  }

  set listScrollTop(top: number) {
    if (this.$scrollable) {
      this.$scrollable.scrollTo(top, "instant", true, true);
    } else {
      this.$contentViewport.scrollTop = top;
    }
  }

  renormalize(): void {
    if (!this._scrollEngine || !this.$scrollable || !this.size) return;
    const engine = this._scrollEngine;
    const vp = engine.viewport;

    const anchorIdx = vp.visible.indexRange.start;
    if (anchorIdx == null) return;
    const anchorOffset = engine.window.getOffset(anchorIdx);
    if (anchorOffset == null) return;
    const anchorVisualPos = anchorOffset - this.$scrollable.contentOffset;

    engine.window.offsetBase = 0;
    engine.window.recalculateOffsetsFrom(0);

    const newAnchorOffset = engine.window.getOffset(anchorIdx);
    if (newAnchorOffset == null) return;
    const newContentOffset = Math.max(0, newAnchorOffset - anchorVisualPos);

    this.updateOverflowBanner(0);
    this.delegate?.onNegativeOverflow?.(0);
    this.$scrollable.scrollTo(newContentOffset, "instant", true, true);
    this.updateMaxContentOffset();

    // Re-render visible cells at correct offsets
    engine.invalidateHeight(0, false);
  }

  getScrollInfo(): Option<{
    left: number;
    top: number;
    scrollHeight: number;
  }> {
    if (!this.$scrollable) return null;
    const raw = this.$scrollable.getScrollInfo();
    if (!raw) return null;
    return {
      left: raw.left,
      top: raw.top,
      scrollHeight: this.computeLogicalHeight(),
    };
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        overflow: hidden;
      }

      #root {
        height: 100%;
        width: 100%;
        position: relative;
      }

      #content-viewport {
        height: 100%;
        width: 100%;
        overflow-y: scroll;
        overscroll-behavior-y: none;
        box-sizing: border-box;
        padding-top: var(--scroll-padding-top, 0px);
        padding-bottom: var(--scroll-padding-bottom, 0px);
        position: relative;
        z-index: 0;
      }

      #content-viewport:not([show-scrollbar]) {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }

      #content-viewport:not([show-scrollbar])::-webkit-scrollbar {
        display: none;
      }

      slot::slotted(*) {
        position: absolute;
        left: 0px;
        z-index: calc(8388635 + var(--idx) + var(--z, 0));
        transform: translateY(var(--offset));
        height: var(--default-cell-length);
        width: 100%;
      }

      slot::slotted([recycled]) {
        opacity: 0;
        pointer-events: none;
      }

      slot::slotted([unmeasured]) {
        visibility: hidden;
      }

      #content {
        position: relative;
        z-index: 0;
      }

      #overflow-banner {
        display: none;
        position: absolute;
        top: var(--thread-top-inset, 60px);
        left: 50%;
        transform: translateX(-50%);
        z-index: 1;
        padding: 12px 16px;
        text-align: center;
        cursor: pointer;
        border-radius: 999px;
        transition: opacity 0.3s ease;
        white-space: nowrap;
        box-shadow: var(--box-shadow);
        background: var(--bg-tooltip);
        color: var(--fg-tooltip);
      }
      #overflow-banner p {
        --font-size: var(--text-10px);
        --margin-top: 0px;
        --margin-bottom: 0px;
        color: inherit;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id=root>
      <div id=content-viewport>
        <div id="overflow-banner"><p>Show earlier messages</p></div>
        <div id=content>
          <slot></slot>
        </div>
      </div>
    </div>
  `;
}

export namespace ScrollVertical {
  export type Props = {
    cellHeightPx?: Option<number>;
    maxIndex?: Option<number>;
    minIndex?: Option<number>;
    initialIndex?: Option<number>;
    topOccludedPx?: Option<number>;
    bottomOccludedPx?: Option<number>;
    /**
     * The content behind the indices changed, not just the geometry: force
     * the reconcile to re-add (and so rebind) the whole rendered range.
     * Without it a bind whose geometry is unchanged keeps every surviving
     * cell's PREVIOUS content — geometry equality is all the engine checks
     * (`ScrollViewport.Props.equals` reads occlusion + length only), so a
     * same-shape dataset swap renders stale cells.
     */
    contextDirtied?: Option<boolean>;
  };

  export type Delegate<T extends HTMLElement> = {
    onCreate: () => T;
    onBind: (
      $e: T,
      index: number,
      heightPx: number,
      resizeOnly: boolean,
    ) => void;
    onRecycle?: ($e: T) => void;
    onScroll?: Option<
      (info: { left: number; top: number; scrollHeight: number }) => void
    >;
    onNegativeOverflow?: (overflow: number) => void;
    options?: ScrollEngine.OptionalCallbacks<T>;
  };
}
